/**
 * Meta Pixel dispatch (#767, #867): only the standard events in `META_STANDARD_EVENTS` are sent; everything else is dropped.
 *
 * Parameters are ALLOWLISTED: generic analytics events carry free-form fields (email, phone,
 * names, labels, ids) that must never reach Meta. Only keys listed here are forwarded.
 */

/**
 * #867: the "Blueticks 2026" dataset receives STANDARD conversion events only. UI-level events
 * (logins, clicks, ecommerce funnel steps, ...) stay in GA4 and are dropped here, never sent as custom events.
 */
export const META_STANDARD_EVENTS: ReadonlySet<string> = new Set([
  'PageView', 'CompleteRegistration', 'StartTrial', 'Purchase', 'Lead',
]);

export function isMetaEventAllowed(eventName: string): boolean {
  return META_STANDARD_EVENTS.has(eventName);
}

/** The pixel loads on the production hosts only: not on netlify previews, localhost or staging. */
const META_PIXEL_HOSTS: ReadonlySet<string> = new Set(['app.blueticks.co']);

export function isMetaPixelHost(hostname: string): boolean {
  return META_PIXEL_HOSTS.has(hostname);
}

/** Meta standard parameters + the two non-identifying login fields. Nothing else leaves the browser. */
export const META_ALLOWED_PARAMS: ReadonlySet<string> = new Set([
  'value', 'currency', 'content_name', 'content_ids', 'content_type', 'content_category',
  'contents', 'num_items', 'status', 'predicted_ltv', 'signup_method', 'is_new_user',
]);

type Fbq = (command: string, eventName: string, params?: Record<string, unknown>, options?: { eventID: string }) => void;

/** Event parameters the pixel may carry (allowlisted again at send time). */
export type MetaEventParams = Readonly<Record<string, unknown>>;

/** `eventId` is the dedup key shared with the server (CAPI) copy: purchase_<subId>, reg_<userId>, trial_<subId>. */
export function sendMetaEvent(fbq: Fbq, eventName: string, params: MetaEventParams = {}, eventId?: string): void {
  const safe: Record<string, unknown> = {};
  for (const key of Object.keys(params)) {
    if (META_ALLOWED_PARAMS.has(key)) safe[key] = params[key];
  }
  if (!isMetaEventAllowed(eventName)) return;
  if (eventId) fbq('track', eventName, safe, { eventID: eventId });
  else fbq('track', eventName, safe);
}
